import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import {
  MousePointer2,
  Hand,
  Lock,
  Unlock,
  Undo2,
  Redo2,
  Map,
  ChevronDown,
  Spline,
  Route,
  LayoutGrid,
  Ellipsis,
  StickyNote,
  Check,
} from 'lucide-react';
import { useReactFlow } from '@xyflow/react';
import { useNodeStore } from '@/stores/node-store';
import { useEdgeLayoutStore } from '@/stores/edge-layout-store';
import {
  useKeyboardShortcuts,
  getModifierKeyDisplay,
} from '@/hooks/use-keyboard-shortcuts';
import { useAnalytics } from '@/hooks/use-analytics';
import { isCondensedLayout } from '@/constants/workflow-editor';
import { StickyNoteColor, STICKY_NOTE_COLOR_OPTIONS } from '@/types/node-types';
import { cn } from '@/lib/utils';

export type InteractionMode = 'mouse' | 'hand' | 'sticky-note';

const STICKY_NOTE_COLORS = Object.keys(
  STICKY_NOTE_COLOR_OPTIONS,
) as StickyNoteColor[];

export interface MouseControlBarProps {
  mode: InteractionMode;
  onModeChange: (mode: InteractionMode) => void;
  isLocked: boolean;
  isWorkflowLocked?: boolean;
  onToggleLock: () => void;
  isOwner: boolean;
  onCloneWorkflow: () => void;
  setIsMiniMapVisible: () => void;
  onAutoLayout?: () => void;
  /** Total canvas container width in pixels – used to decide condensed mode */
  panelGroupWidth?: number;
}

export const MouseControlBar: React.FC<MouseControlBarProps> = ({
  mode,
  onModeChange,
  isLocked,
  isWorkflowLocked = false,
  onToggleLock,
  isOwner: _isOwner,
  onCloneWorkflow: _onCloneWorkflow,
  setIsMiniMapVisible,
  onAutoLayout,
  panelGroupWidth,
}) => {
  const { undo, redo, canUndo, canRedo } = useNodeStore();
  const pendingStickyNoteColor = useNodeStore(
    (state) => state.pendingStickyNoteColor,
  );
  const setPendingStickyNoteColor = useNodeStore(
    (state) => state.setPendingStickyNoteColor,
  );
  const { layout: edgeLayout, toggle: toggleEdgeLayout } = useEdgeLayoutStore();
  const { track } = useAnalytics();

  const handleToggleLock = useCallback(() => {
    track('workflow_lock_toggled', { locked: !isLocked });
    onToggleLock();
  }, [track, isLocked, onToggleLock]);
  const { zoomIn, zoomOut, fitView, getZoom, getViewport, setViewport } =
    useReactFlow();
  const [zoom, setZoomState] = useState(100);
  const [isCondensedMenuOpen, setIsCondensedMenuOpen] = useState(false);
  const [isColorBarOpen, setIsColorBarOpen] = useState(false);

  const isCondensed = isCondensedLayout(panelGroupWidth);
  const isStickyNoteMode = mode === 'sticky-note';

  // Optional color change while in placement mode. Canvas click drops a note
  // with the pending color and auto-reverts to 'mouse'.
  const handlePickStickyColor = useCallback(
    (color: StickyNoteColor) => {
      setPendingStickyNoteColor(color);
    },
    [setPendingStickyNoteColor],
  );

  const handleToggleStickyNote = useCallback(() => {
    if (isStickyNoteMode) {
      // Already in sticky mode — exit back to mouse.
      onModeChange('mouse');
      setIsColorBarOpen(false);
      return;
    }
    onModeChange('sticky-note');
    setIsColorBarOpen(true);
  }, [isStickyNoteMode, onModeChange]);

  // If the canvas leaves sticky-note mode (e.g. user clicked Mouse/Hand
  // or onPaneClick auto-reverted after placement), make sure the color
  // bar isn't left dangling open.
  useEffect(() => {
    if (!isStickyNoteMode && isColorBarOpen) setIsColorBarOpen(false);
    // We only want to close on mode transitions, not on local toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Define zoom handler functions with useCallback
  const handleZoomIn = useCallback(() => {
    zoomIn();
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut();
  }, [zoomOut]);

  const handleZoomTo100 = useCallback(() => {
    const viewport = getViewport();
    setViewport({ ...viewport, zoom: 1 });
    setZoomState(100);
  }, [getViewport, setViewport]);

  const handleZoomToFit = useCallback(() => {
    fitView({ padding: 0.1 });
  }, [fitView]);

  // Track zoom level changes on mount and when zoom functions are called
  useEffect(() => {
    const updateZoom = () => {
      const currentZoom = getZoom();
      setZoomState(Math.round(currentZoom * 100));
    };
    updateZoom();

    // Poll for zoom changes to catch mouse pad drag zooming
    let animationFrameId: number;
    let lastZoom = getZoom();

    const checkZoom = () => {
      const currentZoom = getZoom();
      if (Math.abs(currentZoom - lastZoom) > 0.001) {
        lastZoom = currentZoom;
        setZoomState(Math.round(currentZoom * 100));
      }
      animationFrameId = requestAnimationFrame(checkZoom);
    };

    animationFrameId = requestAnimationFrame(checkZoom);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [getZoom]);

  // Handle keyboard shortcuts for zoom controls
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: ['+', '='],
        handler: () => handleZoomIn(),
      },
      {
        key: ['-', '_'],
        handler: () => handleZoomOut(),
      },
      {
        key: '0',
        handler: () => handleZoomTo100(),
      },
      {
        key: '1',
        handler: () => handleZoomToFit(),
      },
    ],
  });

  const cmdKey = getModifierKeyDisplay();

  const handleUndo = () => {
    if (canUndo()) {
      undo();
    }
  };

  const handleRedo = () => {
    if (canRedo()) {
      redo();
    }
  };

  /**
   * Color picker bar shown while sticky-note placement mode is active.
   * Clicking the StickyNote button enters placement mode immediately (default
   * color); swatches here only change the pending color before canvas drop.
   */
  const stickyNoteColorBar = isColorBarOpen ? (
    <div
      className='flex items-center gap-1.5 rounded-lg p-1.5 shadow-lg border border-border bg-card'
      role='toolbar'
      aria-label='Sticky note color picker'
    >
      {STICKY_NOTE_COLORS.map((color) => {
        const { bgColor, borderColor } = STICKY_NOTE_COLOR_OPTIONS[color];
        const active = pendingStickyNoteColor === color;
        return (
          <Tooltip key={color}>
            <TooltipTrigger asChild>
              <button
                type='button'
                aria-label={`Pick ${color} sticky note`}
                onClick={() => handlePickStickyColor(color)}
                className={cn(
                  'flex size-6 items-center justify-center rounded-full border',
                  active && 'ring-2 ring-primary ring-offset-1',
                )}
                style={{ backgroundColor: bgColor, borderColor: borderColor }}
              >
                {active && (
                  <Check className='size-3.5 text-black/70' strokeWidth={3} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side='top'>
              <p className='capitalize'>{color}</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  ) : null;

  // ── Condensed toolbar ────────────────────────────────────────────────────
  // When the canvas is narrow the full bar would overlap the ChatShortcutBar
  // (bottom-left) and UtilityPanel (bottom-right). Show a compact version:
  // an overflow "…" dropdown for all secondary actions + mode & lock buttons.
  if (isCondensed) {
    return (
      <div className='flex flex-col items-center gap-2'>
        {stickyNoteColorBar}
        <DropdownMenu
          open={isCondensedMenuOpen}
          onOpenChange={(v) => {
            if (!v) setIsCondensedMenuOpen(false);
          }}
        >
          <DropdownMenuTrigger asChild>
            <div className='flex items-center gap-2 rounded-lg p-1.5 shadow-lg border border-border bg-card h-12 cursor-default outline-none'>
              {/* Ellipsis button — explicitly toggles the menu */}
              <Button
                variant='ghost'
                size='sm'
                className='p-1.5 hover:bg-muted transition-colors rounded-l-md rounded-r-none'
                aria-label='More canvas options'
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCondensedMenuOpen((v) => !v);
                }}
              >
                <Ellipsis className='size-5 text-foreground' />
              </Button>

              {/* Separator */}
              <div className='w-px h-5 bg-border mx-0.5' />

              {/* Mouse Mode Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    className={`p-1.5 rounded-none transition-colors ${
                      mode === 'mouse'
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => onModeChange('mouse')}
                  >
                    <MousePointer2 className='size-5' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='top'>
                  <p>Mouse Mode</p>
                </TooltipContent>
              </Tooltip>

              {/* Hand Mode Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    className={`p-1.5 rounded-none transition-colors ${
                      mode === 'hand'
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => onModeChange('hand')}
                  >
                    <Hand className='size-5' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='top'>
                  <p>{isLocked ? 'Disabled when locked' : 'Hand Mode'}</p>
                </TooltipContent>
              </Tooltip>

              {/* Sticky Note Mode Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    className={cn(
                      'p-1.5 rounded-r-md rounded-l-none transition-colors',
                      isStickyNoteMode
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'hover:bg-muted',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleStickyNote();
                    }}
                    disabled={isLocked || isWorkflowLocked}
                  >
                    <StickyNote className='size-5' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='top'>
                  <p>Sticky Note</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align='center'
            side='top'
            className='w-60'
            sideOffset={10}
          >
            {/* Zoom submenu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className='flex items-center justify-between'>
                <span>Zoom: {zoom}%</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className='w-48'>
                <DropdownMenuItem
                  onClick={handleZoomIn}
                  className='flex items-center justify-between'
                >
                  <span>Zoom in</span>
                  <span className='text-xs text-muted-foreground'>
                    {cmdKey} +
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleZoomOut}
                  className='flex items-center justify-between'
                >
                  <span>Zoom out</span>
                  <span className='text-xs text-muted-foreground'>
                    {cmdKey} -
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleZoomTo100}
                  className='flex items-center justify-between'
                >
                  <span>Zoom to 100%</span>
                  <span className='text-xs text-muted-foreground'>
                    {cmdKey} 0
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleZoomToFit}
                  className='flex items-center justify-between'
                >
                  <span>Zoom to fit</span>
                  <span className='text-xs text-muted-foreground'>
                    {cmdKey} 1
                  </span>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={setIsMiniMapVisible}
              className='flex items-center gap-2'
            >
              <Map className='size-4' />
              <span>Mini Map</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={toggleEdgeLayout}
              className='flex items-center gap-2'
            >
              {edgeLayout === 'curved' ? (
                <Spline className='size-4' />
              ) : (
                <Route className='size-4 text-primary' />
              )}
              <span>
                {edgeLayout === 'curved'
                  ? 'Switch to orthogonal edges'
                  : 'Switch to curved edges'}
              </span>
            </DropdownMenuItem>

            {onAutoLayout && (
              <DropdownMenuItem
                onClick={onAutoLayout}
                disabled={isLocked || isWorkflowLocked}
                className='flex items-center gap-2'
              >
                <LayoutGrid className='size-4' />
                <span>Auto layout nodes</span>
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={handleToggleLock}
              className='flex items-center gap-2'
            >
              {isLocked ? (
                <Lock className='size-4' />
              ) : (
                <Unlock className='size-4' />
              )}
              <span>{isLocked ? 'Unlock Canvas' : 'Lock Canvas'}</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={handleUndo}
              disabled={!canUndo() || isLocked || isWorkflowLocked}
              className='flex items-center gap-2'
            >
              <Undo2 className='size-4' />
              <span>Undo</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={handleRedo}
              disabled={!canRedo() || isLocked || isWorkflowLocked}
              className='flex items-center gap-2'
            >
              <Redo2 className='size-4' />
              <span>Redo</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  // ── Full toolbar ─────────────────────────────────────────────────────────
  return (
    <div className='flex flex-col items-center gap-2'>
      {stickyNoteColorBar}
      {/* TEMPORARILY DISABLED: Non-owner message */}
      {/* TODO: Re-enable non-owner message later */}
      {/* {showNonOwnerMessage && (
        <div className='text-xs text-muted-foreground bg-card px-3 py-1.5 rounded-md border border-red-400'>
          <p className='inline'>
            You are not the owner of this workflow. You have to{' '}
            <Button
              variant='link'
              onClick={onCloneWorkflow}
              aria-label='clone the workflow to edit it'
              className='text-primary hover:text-primary/80 underline font-medium cursor-pointer p-2'
            >
              clone
            </Button>{' '}
            the workflow to edit.
          </p>
        </div>
      )} */}

      <div className='flex items-center gap-2 rounded-lg p-1.5 shadow-lg border border-border bg-card h-12'>
        {/* Mini Map */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className='p-1.5 hover:bg-muted transition-colors rounded-l-md rounded-r-none'
              onClick={() => setIsMiniMapVisible()}
            >
              <Map className='size-5 text-foreground' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>Mini Map</p>
          </TooltipContent>
        </Tooltip>

        {/* Edge Layout Toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className='p-1.5 hover:bg-muted transition-colors rounded-none'
              onClick={toggleEdgeLayout}
            >
              {edgeLayout === 'curved' ? (
                <Spline className='size-5 text-foreground' />
              ) : (
                <Route className='size-5 text-primary' />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>
              {edgeLayout === 'curved'
                ? 'Switch to orthogonal edges'
                : 'Switch to curved edges'}
            </p>
          </TooltipContent>
        </Tooltip>

        {/* Auto Layout */}
        {onAutoLayout && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size='sm'
                className='p-1.5 hover:bg-muted transition-colors rounded-none'
                onClick={onAutoLayout}
                disabled={isLocked || isWorkflowLocked}
              >
                <LayoutGrid className='size-5 text-foreground' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='top'>
              <p>Auto-layout nodes</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Zoom Percentage Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className='px-2 py-1.5 hover:bg-muted transition-colors rounded-none'
            >
              <span className='text-sm font-medium inline-block w-8 text-right'>
                {zoom}%
              </span>
              <ChevronDown className='w-4 h-4 ml-1' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='start' className='w-56'>
            <DropdownMenuItem
              onClick={handleZoomIn}
              className='flex items-center justify-between'
            >
              <span>Zoom in</span>
              <span className='text-xs text-muted-foreground'>{cmdKey} +</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleZoomOut}
              className='flex items-center justify-between'
            >
              <span>Zoom out</span>
              <span className='text-xs text-muted-foreground'>{cmdKey} -</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleZoomTo100}
              className='flex items-center justify-between'
            >
              <span>Zoom to 100%</span>
              <span className='text-xs text-muted-foreground'>{cmdKey} 0</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleZoomToFit}
              className='flex items-center justify-between'
            >
              <span>Zoom to fit</span>
              <span className='text-xs text-muted-foreground'>{cmdKey} 1</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Separator */}
        <div className='w-px h-5 bg-border mx-0.5' />

        {/* Mouse Mode Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className={`p-1.5 rounded-none transition-colors ${
                mode === 'mouse'
                  ? 'bg-primary/10 text-primary hover:bg-primary/20'
                  : 'hover:bg-muted'
              }`}
              onClick={() => onModeChange('mouse')}
            >
              <MousePointer2 className='size-5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>Mouse Mode</p>
          </TooltipContent>
        </Tooltip>

        {/* Hand Mode Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className={`p-1.5 rounded-none transition-colors disabled:opacity-50 ${
                mode === 'hand'
                  ? 'bg-primary/10 text-primary hover:bg-primary/20'
                  : 'hover:bg-muted'
              }`}
              onClick={() => onModeChange('hand')}
            >
              <Hand className='size-5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{isLocked ? 'Disabled when locked' : 'Hand Mode'}</p>
          </TooltipContent>
        </Tooltip>

        {/* Sticky Note Mode Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className={cn(
                'p-1.5 rounded-none transition-colors disabled:opacity-50',
                isStickyNoteMode
                  ? 'bg-primary/10 text-primary hover:bg-primary/20'
                  : 'hover:bg-muted',
              )}
              onClick={handleToggleStickyNote}
              disabled={isLocked || isWorkflowLocked}
            >
              <StickyNote className='size-5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>
              {isStickyNoteMode
                ? 'Click canvas to drop — Esc to cancel'
                : 'Add sticky note in canvas'}
            </p>
          </TooltipContent>
        </Tooltip>

        {/* Lock/Unlock Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={isLocked ? 'destructive' : 'ghost'}
              size='sm'
              className={`p-1.5 rounded-none transition-colors ${
                isLocked
                  ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                  : 'hover:bg-muted'
              }`}
              onClick={handleToggleLock}
            >
              {isLocked ? (
                <Lock className='size-5' />
              ) : (
                <Unlock className='size-5' />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{isLocked ? 'Unlock Canvas' : 'Lock Canvas'}</p>
          </TooltipContent>
        </Tooltip>

        {/* Separator */}
        <div className='w-px h-5 bg-border mx-0.5' />

        {/* Undo Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className='p-1.5 rounded-none transition-colors disabled:opacity-50 hover:bg-muted'
              onClick={handleUndo}
              disabled={!canUndo() || isLocked || isWorkflowLocked}
            >
              <Undo2 className='size-5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>Undo</p>
          </TooltipContent>
        </Tooltip>

        {/* Redo Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className='p-1.5 rounded-r-md rounded-l-none transition-colors disabled:opacity-50 hover:bg-muted'
              onClick={handleRedo}
              disabled={!canRedo() || isLocked || isWorkflowLocked}
            >
              <Redo2 className='size-5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>Redo</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
